import { STEPPER_GLOBAL_OPTIONS, StepperSelectionEvent } from '@angular/cdk/stepper';
import { ChangeDetectionStrategy, Component, Inject, OnInit, ViewChild } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { MAT_BOTTOM_SHEET_DATA, MatBottomSheetRef } from '@angular/material/bottom-sheet';
import { MatDialog } from '@angular/material/dialog';
import { MatStepper } from '@angular/material/stepper';
import { CommonValidator } from '@x/common/form';
import { TSearchQuery } from '@x/common/form/components/address-autocomplete/address-autocomplete';
import { Operation, OperationObserverService } from '@x/common/operation';
import { TEDialogClose, TEDialogOpen } from '@x/common/tracking/event-models/dialog.events';
import {
  TEFormAddressSearch,
  TEFormAddressSearchSelectResult,
  TEFormFieldUpdate,
  TEFormMapMovePin,
  TEFormStepChange,
} from '@x/common/tracking/event-models/form.events';
import { TrackingService } from '@x/common/tracking/tracking.service';
import { AddressCloseConfirmationDialogComponent } from '@x/ecommerce-shop/app/core/address/components/address-close-confirmation-dialog/address-close-confirmation-dialog.component';
import {
  AddressBottomSheetData,
  AddressService,
} from '@x/ecommerce-shop/app/core/address/services/address.service';
import { IShopConfig, SHOP_CONFIG } from '@x/ecommerce-shop/config/shop.config';
import { IShopAddressInput } from '@x/ecommerce/shop-client';
import { IExpandedAddress, ILocateAddressResult } from '@x/geocode/client';
import { AddressType, PointInput, ShopAddressInput } from '@x/schemas/ecommerce';
import { BehaviorSubject, Observable, Subject, firstValueFrom, switchMap } from 'rxjs';
import { filter, startWith, takeUntil, tap } from 'rxjs/operators';

type AddressFormGroup<T> = { [K in keyof T]: FormControl<T[K]> };

@Component({
  selector: 'x-address-form-bottom-sheet',
  templateUrl: './address-form-bottom-sheet.component.html',
  styleUrls: ['./address-form-bottom-sheet.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: { class: 'x-address-form-bottom-sheet' },
  providers: [
    {
      provide: STEPPER_GLOBAL_OPTIONS,
      useValue: { displayDefaultIndicatorType: false },
    },
  ],
})
export class AddressFormBottomSheetComponent implements OnInit {
  private _destroy$ = new Subject<void>();
  private _nextStep$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

  addressSearchControl = new FormControl<IExpandedAddress | null>(null);

  countryControl = new FormControl<string>('', {
    nonNullable: true,
    validators: Validators.required,
  });

  provinceControlNotRequiredCountries = ['GB'];
  provinceControl = new FormControl(null);

  instructionsMaxLength = this.shopConfig.features.addressing?.instructionsMaxLength ?? 250;

  addressFormGroup = new FormGroup<AddressFormGroup<IShopAddressInput>>({
    type: new FormControl(null),
    alias: new FormControl(null),
    firstName: new FormControl(null, Validators.required),
    lastName: new FormControl(null, Validators.required),
    email: new FormControl(null, CommonValidator.email()),
    phoneNumber: new FormControl(null, [
      Validators.required,
      Validators.minLength(10),
      Validators.maxLength(16),
    ]),
    complex: new FormControl(),
    businessName: new FormControl(),
    street: new FormControl(null, Validators.required),
    suburb: new FormControl(null),
    city: new FormControl(null, Validators.required),
    postalCode: new FormControl(null, Validators.required),
    province: this.provinceControl,
    country: this.countryControl,
    coordinates: new FormControl(null),
    instructions: new FormControl(null, Validators.maxLength(this.instructionsMaxLength)),
  });

  get addressLabel() {
    const { value } = this.countryControl;

    if (value && value === 'GB') return 'Search for your postcode...';

    return 'Search for your address...';
  }

  validateAddressOperation$: Observable<Operation<ILocateAddressResult | undefined>> =
    this._nextStep$.pipe(
      filter((validate) => validate && this.addressFormGroup.valid),
      switchMap(() =>
        this.operationService.observe(
          this.addressService.locateAddress(this.addressFormGroup.value as IShopAddressInput),
        ),
      ),
      startWith(Operation.create()),
      tap(({ data }) => {
        if (!data) return;

        const { lat, lng } = data;

        if (lat && lng && this.shouldUpdateCoordinates) {
          this.addressFormGroup.patchValue({ coordinates: { lat, lng } }, { emitEvent: false });
        }

        this.stepper?.next();
      }),
    );

  @ViewChild('stepper') stepper: MatStepper;

  get activeStep() {
    return this.stepper?.selectedIndex;
  }

  get shouldUpdateCoordinates(): boolean {
    const fieldToCheck: Array<keyof ShopAddressInput> = [
      'country',
      'postalCode',
      'province',
      'city',
      'suburb',
      'complex',
      'street',
      'businessName',
    ];

    if (!this.data.address) return true;

    return fieldToCheck.some((field) => {
      return this.data.address?.[field] !== this.addressFormGroup.value[field];
    });
  }

  get provinceRequired(): boolean {
    return !this.provinceControlNotRequiredCountries.includes(this.countryControl.value);
  }

  constructor(
    private _bottomSheetRef: MatBottomSheetRef<AddressFormBottomSheetComponent>,
    @Inject(MAT_BOTTOM_SHEET_DATA) public data: AddressBottomSheetData,
    private operationService: OperationObserverService,
    private addressService: AddressService,
    private analyticsService: TrackingService,
    private matDialog: MatDialog,
    @Inject(SHOP_CONFIG) private shopConfig: IShopConfig,
  ) {}

  ngOnInit() {
    // Analytics Event
    this.analyticsService.sendEvent(
      new TEDialogOpen(this.data.origin, {
        dialogIntent: this.data.address ? 'Update Address' : 'Add Address',
        dialogType: 'Address',
      }),
    );

    const { address, defaultCountry } = this.data;
    const userToPatchForm = { ...this.data.user, phoneNumber: this.data.user.phone };

    this.addressFormGroup.patchValue(
      {
        ...(address && { ...address }),
        ...(defaultCountry && { country: defaultCountry }),
        ...userToPatchForm,
      },
      { emitEvent: false },
    );

    if (userToPatchForm) {
      this.addressFormGroup.get('firstName')?.markAsTouched();
      this.addressFormGroup.get('lastName')?.markAsTouched();
      this.addressFormGroup.get('phoneNumber')?.markAsTouched();
    }

    if (address) {
      this.addressFormGroup.markAllAsTouched();
    }

    // address search
    this.addressSearchControl.valueChanges
      .pipe(
        tap((address) => {
          if (!address) return;

          // Analytics Event
          this.analyticsService.sendEvent(
            new TEFormAddressSearchSelectResult('Address', {
              result: address.full,
            }),
          );

          const shopAddressInput: IShopAddressInput =
            this.addressService.getAddressInputFromExpandedAddress(address);
          this.addressFormGroup.patchValue(shopAddressInput, { emitEvent: false });
          this.addressFormGroup.markAllAsTouched();
        }),
        takeUntil(this._destroy$),
      )
      .subscribe();

    // reset form on country update
    this.countryControl.valueChanges
      .pipe(
        tap((countryCode) => {
          if (countryCode) {
            this.addressSearchControl.reset(null, { emitEvent: false });
            this.provinceControl.reset(null, { emitEvent: false });
            this.addressFormGroup.reset(
              { ...userToPatchForm, type: AddressType.Physical, country: countryCode },
              { emitEvent: false },
            );

            if (this.provinceControlNotRequiredCountries.includes(countryCode)) {
              this.provinceControl.removeValidators(Validators.required);
              this.provinceControl.updateValueAndValidity();
            } else {
              this.provinceControl.setValidators(Validators.required);
              this.provinceControl.updateValueAndValidity();
            }
          }
        }),
        takeUntil(this._destroy$),
      )
      .subscribe();
  }

  async confirmCloseBottomSheet() {
    const { dirty, touched } = this.addressFormGroup;

    if (dirty && touched) {
      const response = await firstValueFrom(
        this.matDialog
          .open<
            AddressCloseConfirmationDialogComponent,
            any,
            string
          >(AddressCloseConfirmationDialogComponent)
          .afterClosed(),
      );

      if (response) this.closeBottomSheet();

      return;
    }

    this.closeBottomSheet();
  }

  submit() {
    const { value, valid } = this.addressFormGroup;
    if (!valid) return;

    // Analytics Event
    this.analyticsService.sendEvent(
      new TEDialogClose(this.data.origin, {
        dialogIntent: this.data.address ? 'Update Address' : 'Add Address',
        dialogType: 'Address',
        dialogResponse: 'Positive',
      }),
    );

    this._bottomSheetRef.dismiss(value);
  }

  updateCoordinates(coordinates: PointInput) {
    if (coordinates) {
      this.addressFormGroup.patchValue({ coordinates }, { emitEvent: false });

      // Analytics Event
      this.analyticsService.sendEvent(
        new TEFormMapMovePin('Address', {
          coordinates: `${coordinates.lat}, ${coordinates.lng}`,
        }),
      );
    }
  }

  nextStep(operation: Operation<ILocateAddressResult | undefined>) {
    if (operation.status === 'loading') return;

    this.addressFormGroup.markAllAsTouched();

    if (this.addressFormGroup.invalid) return;

    this._nextStep$.next(true);
  }

  previousStep(stepper: MatStepper) {
    stepper.previous();
  }

  stepChange($event: StepperSelectionEvent) {
    // Analytics Event
    this.analyticsService.sendEvent(
      new TEFormStepChange('Address', {
        stepLabel: $event.selectedStep.label,
      }),
    );
  }

  searchQuery($event: TSearchQuery) {
    // Analytics Event
    this.analyticsService.sendEvent(
      new TEFormAddressSearch('Address', {
        query: $event.query,
        results: $event.suggestions.length > 0,
      }),
    );
  }

  inputChangedEvent(input: HTMLInputElement | HTMLTextAreaElement) {
    // Analytics Event
    this.analyticsService.sendEvent(
      new TEFormFieldUpdate('Address', {
        fieldLabel: input.placeholder.replace('...', ''),
        fieldValue: input.value,
      }),
    );
  }

  private closeBottomSheet() {
    this._bottomSheetRef.dismiss();

    // Analytics Event
    this.analyticsService.sendEvent(
      new TEDialogClose(this.data.origin, {
        dialogIntent: this.data.address ? 'Update Address' : 'Add Address',
        dialogType: 'Address',
        dialogResponse: 'Dismissed',
      }),
    );
  }
}
